import Axios from 'axios';
import * as ReportRequest from '@/models/reportRequest';
import * as ReportResponse from '@/models/reportResponse';
import { ReturnTypes } from '@/enums/returnTypes';
import { PublicTypes, getPublicTypeName } from '@/enums/publicTypes';
import { ReadTypes } from '@/enums/readTypes';

export type Report = ReportResponse.ReportSearch;

export interface SearchCondition {
  publicType: PublicTypes;
  readType: ReadTypes;
  studentId: string;
  tutorIdList: string[];
  dateFrom: Date | null;
  dateTo: Date | null;
  keyWord: string;
  perPage: number;
  page: number;
  sort: string;
}

interface State {
  loading: boolean;
  countAll: number;
  reportList: Report[];
  searchCondition: SearchCondition;
  selectedReport: Report | null;
  editReportList: Report[];
}
const mapState: State = {
  loading: false,
  countAll: 0,
  reportList: new Array<Report>(),
  searchCondition: {
    publicType: PublicTypes.All,
    readType: ReadTypes.All,
    studentId: '',
    tutorIdList: [],
    dateFrom: null,
    dateTo: null,
    keyWord: '',
    perPage: 20,
    page: 1,
    sort: 'updateDate descending',
  },
  selectedReport: null,
  editReportList: new Array<Report>(),
};

const mapActions = {
  async searchReports({ dispatch, commit }: any, searchCondition: SearchCondition) {
    commit('setLoading', true);
    const data = await dispatch('getReports', searchCondition).catch((error: any) => {
      commit('setLoading', false);
      throw error;
    });
    commit('searchReports', data);
  },
  async getReports({ commit }: any, searchCondition: SearchCondition) {
    const request: ReportRequest.ReportRequest = {
      reportId: undefined,
      sendDate: undefined,
      studentId: searchCondition.studentId === '' ? undefined : searchCondition.studentId,
      lessonStartDate: undefined,
      lessonEndDate: undefined,
      subjectIdList: undefined,
      reportSummary: undefined,
      reportNote: undefined,
      attachments: undefined,
      sharingNote: undefined,
      publicType: searchCondition.publicType,
      readType: searchCondition.readType,
      tutorIdList: searchCondition.tutorIdList,
      dateFrom: searchCondition.dateFrom,
      dateTo: searchCondition.dateTo,
      keyWord: searchCondition.keyWord,
      perPage: searchCondition.perPage,
      page: searchCondition.page,
      sort: searchCondition.sort,
    };
    const response = await Axios.post('/api/Report/SearchList', request).catch((error) => {
      throw error;
    });
    response.data.reportList.forEach((report: any) => {
      if (report.lessonStartDate === report.lessonEndDate) {
        report.lessonEndDate = null;
      }
      if (typeof report.lessonStartDate === 'string') {
        report.lessonStartDate = new Date(report.lessonStartDate + 'Z');
      }
      if (typeof report.lessonEndDate === 'string') {
        report.lessonEndDate = new Date(report.lessonEndDate + 'Z');
      }
      if (typeof report.sendDate === 'string') {
        report.sendDate = new Date(report.sendDate + 'Z');
      }
      if (typeof report.updateDate === 'string') {
        report.updateDate = new Date(report.updateDate + 'Z');
      }
    });
    return response.data;
  },
  async selectReport({ dispatch, commit }: any, reportId: string) {
    commit('setLoading', true);
    const data = await dispatch('getReportDetails', reportId).catch((error: any) => {
      commit('setLoading', false);
      throw error;
    });
    commit('selectReport', data);
  },
  async getReportDetails({ commit }: any, reportId: string) {
    const request: ReportRequest.ReportDetailRequest = {
      reportId,
      sendDate: undefined,
      studentId: undefined,
      publicType: undefined,
      lessonStartDate: undefined,
      lessonEndDate: undefined,
      subjectIdList: undefined,
      reportSummary: undefined,
      reportNote: undefined,
      attachments: undefined,
      sharingNote: undefined,
    };
    const response = await Axios.post('/api/Report/Detail', request).catch((error) => {
      throw error;
    });
    if (response.data.lessonStartDate === response.data.lessonEndDate) {
      response.data.lessonEndDate = null;
    }
    if (typeof response.data.lessonStartDate === 'string') {
      response.data.lessonStartDate = new Date(response.data.lessonStartDate + 'Z');
    }
    if (typeof response.data.lessonEndDate === 'string') {
      response.data.lessonEndDate = new Date(response.data.lessonEndDate + 'Z');
    }
    if (typeof response.data.sendDate === 'string') {
      response.data.sendDate = new Date(response.data.sendDate + 'Z');
    }
    if (typeof response.data.updateDate === 'string') {
      response.data.updateDate = new Date(response.data.updateDate + 'Z');
    }
    return response.data;
  },
  async createReports(
    { commit }: any,
    { reportList, schoolId }: { reportList: Report[], schoolId: string }
  ) {
    // ファイルアップロード
    const urlList: Array<{
        studentId: string,
        url: string,
        name: string
    }> = [];
    for (const report of reportList) {
      for (const file of report.attachments) {
        const formData = new FormData();
        formData.append('file', file.raw as File, file.name);
        try {
          const uploadResponse = await Axios.post(`/api/File/Upload?keyPath=${schoolId}/Report`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          urlList.push({
            studentId: report.studentId,
            url: uploadResponse.data as string,
            name: file.name,
          });
        } catch (error) {
          // TODO:何かしらのエラー処理？
        }
      }
    }

    const request: ReportRequest.ReportsCreateRequest = {
      createReportList: await Promise.all(reportList.map(async (report) => {
        return {
          reportId: report.id,
          sendDate: undefined,
          studentId: report.studentId,
          publicType: report.publicType,
          lessonStartDate: report.lessonStartDate,
          lessonEndDate: report.lessonEndDate,
          subjectIdList: report.subjectList.map((subject) => subject.id),
          reportSummary: report.reportSummary,
          reportNote: report.reportNote,
          attachments: urlList.filter((file) => file.studentId === report.studentId),
          sharingNote: report.sharingNote,
        };
      })),
    };
    const response = await Axios.post('/api/Report/Create', request).catch((error) => {
      throw new Error('指導報告書の登録に失敗しました。');
    });

    return response.data;
  },
  async editReport(
    { commit }: any,
    { report, schoolId }: { report: Report, schoolId: string }
  ) {
    // 既存の有効ファイル
    const urlList = report.attachments
      .filter((file) => !file.isDelete && !!file.url)
      .map((file) => ({
        url: file.url as string,
        name: file.name
      }));

    // 新規ファイル
    for (const file of report.attachments.filter((w) => !w.isDelete && !w.url)) {
      const formData = new FormData();
      formData.append('file', file.raw as File, file.name);
      try {
        const uploadResponse = await Axios.post(`/api/File/Upload?keyPath=${schoolId}/Report`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        urlList.push({
          url: uploadResponse.data as string,
          name: file.name,
        });
      } catch (error) {
        // TODO:何かしらのエラー処理？
      }
    }

    const request: ReportRequest.ReportDetailRequest = {
      reportId: report.id,
      sendDate: undefined,
      studentId: report.studentId,
      publicType: report.publicType,
      lessonStartDate: report.lessonStartDate,
      lessonEndDate: report.lessonEndDate,
      subjectIdList: report.subjectList.map((subject) => subject.id),
      reportSummary: report.reportSummary,
      reportNote: report.reportNote,
      attachments: urlList,
      sharingNote: report.sharingNote,
    };
    const response = await Axios.post('/api/Report/Edit', request).catch((error) => {
      throw new Error('指導報告書の変更に失敗しました。');
    });

    commit('changeReport', report);
    return response.data;
  },
  async deleteReport({ commit }: any, reportId: string) {
    const request: ReportRequest.ReportDetailRequest = {
      reportId,
      sendDate: undefined,
      studentId: undefined,
      publicType: undefined,
      lessonStartDate: undefined,
      lessonEndDate: undefined,
      subjectIdList: undefined,
      reportSummary: undefined,
      reportNote: undefined,
      attachments: undefined,
      sharingNote: undefined,
    };
    const response = await Axios.post('/api/Report/Delete', request).catch((error) => {
      throw new Error('指導報告書の削除に失敗しました。');
    });
    commit('removeReport', reportId);
    return response.data;
  },
  async confirmReport(
    { commit }: any,
    { reportId, sendDate }: { reportId: string, sendDate: Date | null }
  ) {
    const request: ReportRequest.ReportDetailRequest = {
      reportId,
      sendDate,
      studentId: undefined,
      publicType: undefined,
      lessonStartDate: undefined,
      lessonEndDate: undefined,
      subjectIdList: undefined,
      reportSummary: undefined,
      reportNote: undefined,
      attachments: undefined,
      sharingNote: undefined,
    };
    const response = await Axios.post('/api/Report/Approval', request).catch((error) => {
      throw new Error('指導報告書の承認に失敗しました。');
    });
    commit('confirmReport', reportId);
    return response.data;
  },
  async getAttachment({ commit }: any, url: string) {
    const response = await Axios.get(
      url,
      { responseType: 'blob' }
    ).catch((error) => {
      throw error;
    });
    return response.data;
  },
};

const mapMutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  searchReports(state: State, data: ReportResponse.ReportDetailList) {
    state.countAll = data.countAll;
    state.reportList = data.reportList;
    state.loading = false;
  },
  selectReport(state: State, data: ReportResponse.ReportSearch) {
    state.selectedReport = data;
    state.loading = false;
  },
  changeReport(state: State, report: Report) {
    const findIndex = state.reportList.findIndex((value) => {
      return (value.id === report.id);
    });
    if (findIndex >= 0) {
      state.reportList.splice(findIndex, 1, report);
    }
  },
  removeReport(state: State, id: string) {
    const findIndex = state.reportList.findIndex((value) => {
      return (value.id === id);
    });
    if (findIndex >= 0) {
      state.reportList.splice(findIndex, 1);
    }
  },
  confirmReport(state: State, id: string) {
    const report = state.reportList.find((value) => {
      return (value.id === id);
    });
    if (!!report) {
      report.publicType = PublicTypes.Public;
      report.publicTypeName = getPublicTypeName(PublicTypes.Public);
    }
  },
};

export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
